import React from 'react';
import { ECardsFrontVariant } from '@components/ECard';
import { JoykitColor } from '@components/themes';
import { useFindPerfectInvitationSectionController } from '@sections/online-rsvp/FindPerfectInvitationSection/FindPerfectInvitationSection.controller';
import {
  ECardContainer,
  FirstInvitationContainer,
  FollowupInvitationContainer,
  InvitationColumn,
  InvitationContainerColumns,
  MainTextPairing,
  SectionBox,
  StyledECard,
  ExploreDesignTemplatesButtonWrapper,
} from '@sections/online-rsvp/FindPerfectInvitationSection/FindPerfectInvitationSection.styles';
import { LazyLoadWrapper } from '@components/LazyLoadWrapper';
import { CTALink } from '@components/CTALink';
import { useTranslation } from 'react-i18next';

type InvitationSlide = Readonly<{
  ecardVariant: ECardsFrontVariant;
  backgroundColor: JoykitColor;
  textColor: JoykitColor;
  shadowColor: JoykitColor;
}>;

const invitationSlides: ReadonlyArray<InvitationSlide> = [
  {
    ecardVariant: 'blueacrylic',
    backgroundColor: 'blue1',
    textColor: 'blue6',
    shadowColor: 'blue2',
  },
  {
    ecardVariant: 'goldengarden',
    backgroundColor: 'yellow1',
    textColor: 'yellow6',
    shadowColor: 'yellow2',
  },
  {
    ecardVariant: 'botanicalfun',
    backgroundColor: 'green1',
    textColor: 'green6',
    shadowColor: 'green2',
  },
  {
    ecardVariant: 'peonieswoodcut',
    backgroundColor: 'red1',
    textColor: 'red6',
    shadowColor: 'red2',
  },
  {
    ecardVariant: 'goldfoil',
    backgroundColor: 'yellow1',
    textColor: 'yellow6',
    shadowColor: 'yellow2',
  },
  {
    ecardVariant: 'desertdaydream',
    backgroundColor: 'red1',
    textColor: 'red6',
    shadowColor: 'red2',
  },
];

interface Props {}

export const FindPerfectInvitationSection: React.FC<Props> = () => {
  const sectionId = 'Find_the_perfect_invitation';
  const { sectionRef, invitationBoxesRefs, isScrolledIntoView, setScrolledIntoView } = useFindPerfectInvitationSectionController();
  const { t } = useTranslation('findPerfectInvitationsSection');
  const mainText = t('mainText');
  const exploreDesigns = t('exploreDesigns');

  return (
    <SectionBox ref={sectionRef} as={'section'} id={sectionId}>
      <LazyLoadWrapper beforeLoad={() => setScrolledIntoView(true)} threshold={0}>
        <div />
      </LazyLoadWrapper>
      {invitationSlides.map((slide, index) => {
        const CurContainer = index > 0 ? FollowupInvitationContainer : FirstInvitationContainer;
        return (
          <CurContainer key={index} ref={invitationBoxesRefs[index]} backgroundColor={slide.backgroundColor}>
            <InvitationContainerColumns verticalAlign={'center'}>
              <InvitationColumn width="half">
                <ECardContainer>
                  {isScrolledIntoView && (
                    <StyledECard variant={slide.ecardVariant} copyVariant={index + 41} shadowColor={slide.shadowColor} isEagerLoading />
                  )}
                </ECardContainer>
              </InvitationColumn>
              <InvitationColumn width="half">
                <MainTextPairing
                  titleVariant="hed56"
                  bodyVariant="dek20"
                  title={mainText.title}
                  body={mainText.body}
                  titleColor={slide.textColor}
                  bodyColor={slide.textColor}
                  titleTagName="h2"
                />
                <ExploreDesignTemplatesButtonWrapper>
                  <CTALink id={`exploreLinkMore${index}`} href="/wedding-invitations/" hasChevron>
                    {exploreDesigns}
                  </CTALink>
                </ExploreDesignTemplatesButtonWrapper>
              </InvitationColumn>
            </InvitationContainerColumns>
          </CurContainer>
        );
      })}
    </SectionBox>
  );
};
